import { useEffect, useState } from "react";
import { api, API } from "@/lib/apiClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, formatDate } from "@/components/shared";
import { ShieldAlert, Download, Trash2, RotateCcw, Database, ScrollText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function MasterToolsPage() {
  const [sysInfo, setSysInfo] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  // Wipe demo data confirm
  const [wipeScope, setWipeScope] = useState("");           // "" = all parks; else park_id
  const [wipeConfirm, setWipeConfirm] = useState("");
  const [wipeBusy, setWipeBusy] = useState(false);
  // Drop park confirm
  const [dropParkId, setDropParkId] = useState("");
  const [dropConfirm, setDropConfirm] = useState("");
  const [dropBusy, setDropBusy] = useState(false);
  // Soft-deleted parks (for restore)
  const [deletedParks, setDeletedParks] = useState([]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [siRes, auditRes] = await Promise.all([
        api.get("/master/system-info"),
        api.get("/master/audit-log?limit=200"),
      ]);
      setSysInfo(siRes.data);
      setAudit(auditRes.data);
      setDeletedParks((siRes.data.parks_breakdown || []).filter((p) => p.deleted_at));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load master tools");
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);

  const activeParks = (sysInfo?.parks_breakdown || []).filter((p) => !p.deleted_at);
  const dropParkObj = activeParks.find((p) => p.id === dropParkId);

  const runWipeDemo = async () => {
    if (wipeConfirm !== "WIPE DEMO") { toast.error('Type "WIPE DEMO" exactly to confirm.'); return; }
    setWipeBusy(true);
    try {
      const body = { confirmation: wipeConfirm };
      if (wipeScope) body.park_id = wipeScope;
      const { data } = await api.post("/master/wipe-demo-data", body);
      const scopeName = wipeScope
        ? (activeParks.find((p) => p.id === wipeScope)?.name || "park")
        : "ALL parks";
      toast.success(`Wiped ${scopeName}: ${Object.entries(data.deleted).map(([k, v]) => `${v} ${k}`).join(", ")}`);
      setWipeConfirm("");
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Wipe failed"); }
    finally { setWipeBusy(false); }
  };

  const runDropPark = async () => {
    if (!dropParkObj) { toast.error("Select a park to drop."); return; }
    if (dropConfirm !== dropParkObj.name) {
      toast.error(`Type "${dropParkObj.name}" exactly to confirm.`);
      return;
    }
    setDropBusy(true);
    try {
      const { data } = await api.post("/master/drop-park", { park_id: dropParkId, confirmation: dropConfirm });
      toast.success(`Park dropped. Restorable until ${formatDate(data.purge_at)}.`);
      setDropParkId(""); setDropConfirm("");
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Drop failed"); }
    finally { setDropBusy(false); }
  };

  const restorePark = async (pid) => {
    if (!window.confirm("Restore this park?")) return;
    try {
      await api.post(`/master/restore-park/${pid}`);
      toast.success("Park restored.");
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };

  const exportSnapshot = () => {
    // Auth cookie is sent automatically; backend streams a JSON file download.
    window.open(`${API}/master/export-snapshot`, "_blank");
    toast.success("Snapshot download started…");
    // Reload audit after a small delay
    setTimeout(loadAll, 1500);
  };

  return (
    <div className="space-y-6" data-testid="master-tools-page">
      <PageHeader
        title="Master Tools"
        subtitle="Owner-only. Destructive actions are audited and (where possible) reversible."
        icon={ShieldAlert}
        accent="#A13E34"
      />

      {/* SYSTEM INFO */}
      <Card className="border-[#EAEAE5] shadow-none" data-testid="system-info-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Database size={16} className="text-[#284B3B]" />
            <h2 className="text-base font-semibold text-[#1A211D]">System info</h2>
          </div>
          {loading ? (
            <div className="text-sm text-[#737A76]">Loading…</div>
          ) : !sysInfo ? null : (
            <>
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-3 mb-4">
                {Object.entries(sysInfo.counts).map(([k, v]) => (
                  <div key={k} className="bg-[#F5F4EF] rounded px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-[#737A76]">{k}</div>
                    <div className="text-lg font-semibold tabular-nums text-[#1A211D]" data-testid={`sysinfo-${k}`}>{v}</div>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-[#737A76] text-left border-b border-[#EAEAE5]">
                      <th className="py-1.5">Park</th>
                      <th className="py-1.5 text-right">Lots</th>
                      <th className="py-1.5 text-right">Tenants</th>
                      <th className="py-1.5 text-right">Reservations</th>
                      <th className="py-1.5 text-right">Invoices</th>
                      <th className="py-1.5 text-right">Payments</th>
                      <th className="py-1.5">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sysInfo.parks_breakdown.map((p) => (
                      <tr key={p.id} className="border-b border-[#EAEAE5]/60" data-testid={`sysinfo-park-${p.id}`}>
                        <td className="py-1.5 font-medium text-[#1A211D]">{p.name}</td>
                        <td className="py-1.5 text-right tabular-nums">{p.lots}</td>
                        <td className="py-1.5 text-right tabular-nums">{p.tenants}</td>
                        <td className="py-1.5 text-right tabular-nums">{p.reservations}</td>
                        <td className="py-1.5 text-right tabular-nums">{p.invoices}</td>
                        <td className="py-1.5 text-right tabular-nums">{p.payments}</td>
                        <td className="py-1.5">
                          {p.deleted_at ? (
                            <span className="text-[11px] bg-[#FFE4E1] text-[#A13E34] px-2 py-0.5 rounded">
                              Soft-deleted
                            </span>
                          ) : (
                            <span className="text-[11px] bg-[#E3F0E6] text-[#1F6336] px-2 py-0.5 rounded">Active</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* BACKUP / EXPORT */}
      <Card className="border-[#EAEAE5] shadow-none" data-testid="export-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <Download size={16} className="text-[#284B3B]" />
                <h2 className="text-base font-semibold text-[#1A211D]">Backup — Export snapshot</h2>
              </div>
              <p className="text-xs text-[#737A76] mt-1">
                Downloads a JSON file containing all records (users, parks, lots, tenants, reservations, invoices, payments, audit log).
                Recommended: run weekly. Store off-site (Drive, Dropbox, S3).
              </p>
            </div>
            <Button onClick={exportSnapshot} className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" data-testid="export-snapshot-btn">
              <Download size={14} className="mr-1.5" />
              Export snapshot now
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* RESTORE SOFT-DELETED PARKS */}
      {deletedParks.length > 0 && (
        <Card className="border-[#284B3B]/30 bg-[#FFF4D4]/40 shadow-none" data-testid="restore-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <RotateCcw size={16} className="text-[#946A00]" />
              <h2 className="text-base font-semibold text-[#1A211D]">Soft-deleted parks ({deletedParks.length})</h2>
            </div>
            <ul className="space-y-2">
              {deletedParks.map((p) => (
                <li key={p.id} className="flex items-center justify-between bg-white px-3 py-2 rounded border border-[#EAEAE5]" data-testid={`deleted-park-${p.id}`}>
                  <div className="text-sm">
                    <strong>{p.name}</strong>{" "}
                    <span className="text-[#737A76]">— deleted {formatDate(p.deleted_at)}</span>
                  </div>
                  <Button onClick={() => restorePark(p.id)} variant="outline" className="border-[#284B3B] text-[#284B3B]" data-testid={`restore-park-${p.id}`}>
                    <RotateCcw size={14} className="mr-1.5" />
                    Restore
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* DANGER ZONE */}
      <Card className="border-[#A13E34]/40 bg-[#FFE4E1]/30 shadow-none" data-testid="danger-zone">
        <CardContent className="p-5 space-y-6">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-[#A13E34]" />
            <h2 className="text-base font-semibold text-[#A13E34]">Danger zone</h2>
          </div>

          {/* Wipe demo data */}
          <div>
            <h3 className="font-semibold text-[#1A211D] text-sm mb-1">Wipe demo data</h3>
            <p className="text-xs text-[#737A76] mb-3">
              Drops all <strong>tenants, reservations, invoices, and payments</strong>. Lots and park config are preserved.
              Use when a park goes from demo to production.
            </p>
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#737A76]">Scope</Label>
                <select
                  value={wipeScope}
                  onChange={(e) => setWipeScope(e.target.value)}
                  className="block h-9 bg-white border border-[#EAEAE5] rounded px-3 text-sm w-56"
                  data-testid="wipe-scope-select"
                >
                  <option value="">ALL parks (every customer)</option>
                  {activeParks.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#737A76]">Type &quot;WIPE DEMO&quot; to confirm</Label>
                <Input
                  value={wipeConfirm}
                  onChange={(e) => setWipeConfirm(e.target.value)}
                  placeholder="WIPE DEMO"
                  className="h-9 w-48 bg-white border-[#A13E34] font-mono"
                  data-testid="wipe-confirm-input"
                />
              </div>
              <Button
                onClick={runWipeDemo}
                disabled={wipeBusy || wipeConfirm !== "WIPE DEMO"}
                className="h-9 bg-[#A13E34] hover:bg-[#8A2E25] text-white disabled:opacity-50"
                data-testid="wipe-demo-btn"
              >
                <Trash2 size={14} className="mr-1.5" />
                {wipeBusy ? "Wiping…" : "Wipe demo data"}
              </Button>
            </div>
          </div>

          {/* Drop park (soft) */}
          <div className="pt-4 border-t border-[#A13E34]/20">
            <h3 className="font-semibold text-[#1A211D] text-sm mb-1">Drop a park (soft-delete)</h3>
            <p className="text-xs text-[#737A76] mb-3">
              Marks the park and ALL its data as deleted. It&apos;s restorable for <strong>{sysInfo?.soft_delete_days || 30} days</strong>,
              after which it&apos;s permanently purged.
            </p>
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#737A76]">Park</Label>
                <select
                  value={dropParkId}
                  onChange={(e) => { setDropParkId(e.target.value); setDropConfirm(""); }}
                  className="block h-9 bg-white border border-[#EAEAE5] rounded px-3 text-sm w-56"
                  data-testid="drop-park-select"
                >
                  <option value="">— Select park —</option>
                  {activeParks.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#737A76]">
                  Type &quot;{dropParkObj?.name || "park name"}&quot; to confirm
                </Label>
                <Input
                  value={dropConfirm}
                  onChange={(e) => setDropConfirm(e.target.value)}
                  placeholder={dropParkObj?.name || ""}
                  disabled={!dropParkObj}
                  className="h-9 w-56 bg-white border-[#A13E34] font-mono disabled:bg-[#F5F4EF]"
                  data-testid="drop-park-confirm-input"
                />
              </div>
              <Button
                onClick={runDropPark}
                disabled={dropBusy || !dropParkObj || dropConfirm !== dropParkObj?.name}
                className="h-9 bg-[#A13E34] hover:bg-[#8A2E25] text-white disabled:opacity-50"
                data-testid="drop-park-btn"
              >
                <Trash2 size={14} className="mr-1.5" />
                {dropBusy ? "Dropping…" : "Drop park"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AUDIT LOG */}
      <Card className="border-[#EAEAE5] shadow-none" data-testid="audit-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <ScrollText size={16} className="text-[#284B3B]" />
            <h2 className="text-base font-semibold text-[#1A211D]">Audit log</h2>
            <span className="text-xs text-[#737A76]">(last {audit.length})</span>
          </div>
          {audit.length === 0 ? (
            <p className="text-sm text-[#737A76]">No master actions yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="audit-table">
                <thead>
                  <tr className="uppercase tracking-wider text-[#737A76] text-left border-b border-[#EAEAE5]">
                    <th className="py-1.5">When</th>
                    <th className="py-1.5">Who</th>
                    <th className="py-1.5">Action</th>
                    <th className="py-1.5">Target</th>
                    <th className="py-1.5">Summary</th>
                    <th className="py-1.5">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((row) => (
                    <tr key={row.id} className="border-b border-[#EAEAE5]/60 align-top" data-testid={`audit-row-${row.id}`}>
                      <td className="py-1.5 tabular-nums whitespace-nowrap">{formatDate(row.ts)} {new Date(row.ts).toLocaleTimeString()}</td>
                      <td className="py-1.5">{row.actor_email}</td>
                      <td className="py-1.5 font-mono">{row.action}</td>
                      <td className="py-1.5 font-mono text-[#737A76]">{row.target_type}{row.target_id ? `/${row.target_id.slice(-6)}` : ""}</td>
                      <td className="py-1.5">{row.summary}</td>
                      <td className="py-1.5 font-mono text-[#737A76]">{row.ip || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
